import CrudTable from "components/table/CrudTable";
import React, { useEffect, useState } from "react";
import { useToast } from "context/toast-context/ToastProvider";
import ToastMessage from "components/Dialog/ToastMessage";
import { useCookies } from "react-cookie";
import {
  createClassSection,
  deleteClassSection,
  fetchAcademicyears,
  fetchClassSections,
  getConfigOrganization,
  getUsers,
  updateClassSection,
} from "ServerAPI/serverAPI";
import ClassSectionForm from "./ClassSectionForm";

const ClassSection = () => {
  const [rows, setRows] = useState([]);
  const { showToast } = useToast();
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cookies] = useCookies();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [departments, setDepartments] = useState([]);

  const handleToastMessageClose = () => {
    setShowToastMessage(false);
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      headerClassName: "table-header",
      editable: false,
    },
    {
      field: "title",
      headerName: "Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
      headerClassName: "table-header",
      editable: false,
      renderCell: (params) => {
        if (!params.value) return "";
        return params.value
          .toString()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      align: "center",
      headerAlign: "center",
      headerClassName: "table-header",
      editable: false,
      renderCell: (params) => {
        if (!params.value) return "";
        return params.value
          .toString()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
    },
    {
      field: "academicYear",
      headerName: "Academic Year",
      flex: 1,
      align: "center",
      headerAlign: "center",
      headerClassName: "table-header",
      editable: false,
      renderCell: (params) => {
        if (!params.value) return "";
        const academicYear = academicYears.find(
          (a) => a.academicYearId === params.value,
        );
        if (!academicYear) return "";
        return academicYear.name
          .toString()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      headerClassName: "table-header",
      align: "center",
      headerAlign: "center",
      valueGetter: (_, row) => {
        const year = academicYears.find(
          (a) => a.academicYearId === row.academicYear,
        );

        return year?.isCurrent ? "Active" : "Inactive";
      },
      renderCell: (params) => params.value
    },
  ];

  const handlefetchClassSections = async () => {
    const payload = {
      userId: cookies.userId,
    };
    setLoading(true);
    if (payload && payload.userId) {
      const response = await fetchClassSections(payload);
      if (response && response.status === "success") {
        const rowsWithSno = (response.data || []).map((row, index) => ({
          ...row,
          sno: index + 1,
        }));
        setRows(rowsWithSno || []);
        setLoading(false);
      } else {
        setRows([]);
        setLoading(false);
        showToast(
          response?.message || "Failed to fetch Class Sections",
          "error",
        );
      }
    } else {
      setRows([]);
      setLoading(false);
    }
  };

  const handlecreateClassSection = async (data) => {
    const payload = {
      userId: cookies.userId,
      ...data,
    };
    setButtonLoading(true);
    const response = await createClassSection(payload);
    if (response && response.status === "success") {
      setRows((prevRows) => {
        const updated = [response.data, ...prevRows];
        return updated.map((row, index) => ({ ...row, sno: index + 1 }));
      });
      setButtonLoading(false);
      showToast("Class Section Created Successfully", "success");
      return true;
    } else {
      setButtonLoading(false);
      showToast(response?.message || "Failed to create Class Section", "error");
      return false;
    }
  };

  const handleDeleteClassSection = async (data) => {
    const payload = {
      userId: cookies.userId,
      classSectionId: data.classSectionId,
      action: "delete",
    };
    setLoading(true);
    const response = await deleteClassSection(payload);
    if (response && response.status === "success") {
      setRows((prevRows) => {
        const filtered = prevRows.filter(
          (row) => row.classSectionId !== payload.classSectionId,
        );
        return filtered.map((row, index) => ({ ...row, sno: index + 1 }));
      });
      setLoading(false);
      showToast("Class Section Deleted Successfully", "success");
      return true;
    } else {
      setLoading(false);
      showToast(response?.message || "Failed to delete Class Section", "error");
      return false;
    }
  };

  const handleUpdateClassSection = async (data) => {
    const payload = {
      userId: cookies.userId,
      action: "update",
      ...data,
    };
    setButtonLoading(true);
    const response = await updateClassSection(payload);
    if (response && response.status === "success") {
      setRows((prevRows) => {
        const updated = prevRows.map((row) => {
          if (row.classSectionId === data.classSectionId) {
            return response.data;
          }
          return row;
        });
        return updated.map((row, index) => ({ ...row, sno: index + 1 }));
      });
      setButtonLoading(false);
      showToast("Class Section Updated Successfully", "success");
      return true;
    } else {
      setButtonLoading(false);
      showToast(response?.message || "Failed to update Class Section", "error");
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classSectionsRes, usersRes, academicYearsRes, configRes] =
          await Promise.all([
            fetchClassSections({ userId: cookies.userId }),
            getUsers(cookies.email, cookies.roleId, cookies.organizationId),
            fetchAcademicyears({ userId: cookies.userId }),
            getConfigOrganization(
              cookies.email,
              cookies.organizationId,
              cookies.userId,
            ),
          ]);

        setDepartments(configRes?.data?.department || []);

        if (classSectionsRes?.status === "success") {
          const rowsWithSno = (classSectionsRes.data || []).map(
            (row, index) => ({
              ...row,
              sno: index + 1,
            }),
          );
          setRows(rowsWithSno);
          setAcademicYears(academicYearsRes?.data || []);
          setCoordinators(
            usersRes?.data?.users?.filter(
              (u) =>
                u?.role?.roleId === "programCoordinator" && u.deleted === false,
            ) || [],
          );
          setParticipants(
            usersRes?.data?.users?.filter(
              (u) =>
                u?.role?.roleId === "courseParticipant" && u.deleted === false,
            ) || [],
          );
        } else {
          setRows([]);
          setAcademicYears([]);
          setCoordinators([]);
          setParticipants([]);
          setDepartments([]);
        }
      } catch (e) {
        setRows([]);
        setAcademicYears([]);
        setCoordinators([]);
        setParticipants([]);
        setDepartments([]);
        showToast("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [cookies]);

  return (
    <div className="container ms-auto my-5">
      <ToastMessage
        open={showToastMessage}
        message="Course Marked As Deleted"
        closeTiming={3000}
        handleClose={handleToastMessageClose}
      />
      <CrudTable
        rows={rows}
        columns={columns}
        onRefresh={handlefetchClassSections}
        loading={loading}
        createButtonText={"Create Section"}
        FormComponent={ClassSectionForm}
        deleteButtonText={"Delete Section"}
        handleSubmit={handlecreateClassSection}
        buttonLoading={buttonLoading}
        getRowId={(row) => row.classSectionId}
        handleDelete={handleDeleteClassSection}
        onUpdate={handleUpdateClassSection}
        disable={cookies.roleId !== "organizationCoordinator"}
        formwidth={700}
        academicYears={
          academicYears?.length > 0
            ? academicYears.find((a) => a.isCurrent)
            : {}
        }
        coordinators={coordinators}
        participants={participants}
        departments={departments}
        resolvers={{
          academicYear: (id) =>
            academicYears.find((a) => a.academicYearId === id)?.name || "",
          sectionCoordinator: (id) =>
            coordinators.find((c) => c.userId === id)?.name || "",
        }}
      />
    </div>
  );
};

export default ClassSection;
